import { notifyError, notifySuccess } from "@/lib/notification";
import * as profileService from "@/services/profile.service";
import {
  ProfileType,
  ProfileUpdateType,
  ProfileContactUpdateType,
} from "@/services/profile.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const profileKey = {
  all: ["profile"] as const,
  detail: () => [...profileKey.all, "detail"] as const,
};

export const useProfileQuery = () =>
  useQuery({
    queryKey: profileKey.detail(),
    queryFn: profileService.fetchProfile,
  });

export const useUpdateProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<ProfileType, AxiosError, ProfileUpdateType>({
    mutationFn: profileService.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey.detail() });
      notifySuccess("Profile details updated");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to update profile";
      notifyError(apiError);
    },
  });
};

export const useUpdateProfileContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError, ProfileContactUpdateType>({
    mutationFn: profileService.updateProfileContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey.detail() });
      notifySuccess("Contact details updated. Please re-login if needed.");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to update contact info";
      notifyError(apiError);
    },
  });
};

export const useUpdateProfilePhotoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<ProfileType, AxiosError, File>({
    mutationFn: profileService.updateProfilePhoto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey.detail() });
      notifySuccess("Profile photo updated");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to upload photo";
      notifyError(apiError);
    },
  });
};






















export const useSkillsQuery = () =>
  useQuery({
    queryKey: [...profileKey.all, "skills"],
    queryFn: profileService.fetchSkills,
  });

export const useAddSkillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<profileService.SkillType, AxiosError, profileService.SkillPayloadType>({
    mutationFn: profileService.addSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKey.all, "skills"] });
      notifySuccess("Skill added successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to add skill";
      notifyError(apiError);
    },
  });
};

export const useUpdateSkillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    profileService.SkillType,
    AxiosError,
    { id: string; data: profileService.SkillPayloadType }
  >({
    mutationFn: ({ id, data }) => profileService.updateSkill(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKey.all, "skills"] });
      notifySuccess("Skill updated successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to update skill";
      notifyError(apiError);
    },
  });
};

export const useDeleteSkillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, AxiosError, string>({
    mutationFn: profileService.deleteSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKey.all, "skills"] });
      notifySuccess("Skill deleted successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to delete skill";
      notifyError(apiError);
    },
  });
};

export const useResumesQuery = () =>
  useQuery({
    queryKey: [...profileKey.all, "resumes"],
    queryFn: profileService.fetchResumes,
  });

export const useUploadResumeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<profileService.ResumeType, AxiosError, File>({
    mutationFn: profileService.uploadResume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKey.all, "resumes"] });
      notifySuccess("Resume uploaded successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to upload resume";
      notifyError(apiError);
    },
  });
};

export const useSetPrimaryResumeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, AxiosError, string>({
    mutationFn: profileService.setPrimaryResume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKey.all, "resumes"] });
      notifySuccess("Primary resume updated successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to set primary resume";
      notifyError(apiError);
    },
  });
};

export const useDeleteResumeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, AxiosError, string>({
    mutationFn: profileService.deleteResume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKey.all, "resumes"] });
      notifySuccess("Resume deleted successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to delete resume";
      notifyError(apiError);
    },
  });
};
